import { createHash } from "node:crypto";
import { ULogger } from "@/app/utils/ULogger";

export class UString {
  private static readonly ESCAPED_UNICODE = /\\u([0-9a-fA-F]{4})/g;
  private static readonly ESCAPED_HEX_ENTITY = /&#x([0-9a-fA-F]+);/g;
  private static readonly ESCAPED_DEC_ENTITY = /&#(\d+);/g;
  private static readonly MIN_DECODABLE_CODE_POINT = 32;
  private static readonly MAX_DECODABLE_CODE_POINT = 0x10ffff;
  private static readonly ESCAPE_LOG_SAMPLE = 160;

  public static decodeEscapedCharacters(text: string): string {
    const decoded = text
      .replace(UString.ESCAPED_UNICODE, (match, hex) =>
        UString.decodedCodePoint(match, Number.parseInt(hex, 16)),
      )
      .replace(UString.ESCAPED_HEX_ENTITY, (match, hex) =>
        UString.decodedCodePoint(match, Number.parseInt(hex, 16)),
      )
      .replace(UString.ESCAPED_DEC_ENTITY, (match, decimal) =>
        UString.decodedCodePoint(match, Number.parseInt(decimal, 10)),
      );

    if (decoded !== text) {
      ULogger.warn(UString.name, "Decoded an escaped character sequence", {
        sample: text.slice(0, UString.ESCAPE_LOG_SAMPLE),
      });
    }

    return decoded;
  }

  private static decodedCodePoint(original: string, code: number): string {
    if (
      !Number.isFinite(code) ||
      code < UString.MIN_DECODABLE_CODE_POINT ||
      code > UString.MAX_DECODABLE_CODE_POINT
    ) {
      return original;
    }
    return String.fromCodePoint(code);
  }

  public static decodeEscapedDeep<T>(value: T): T {
    if (typeof value === "string") {
      return UString.decodeEscapedCharacters(value) as T;
    }
    if (Array.isArray(value)) {
      return value.map((item) => UString.decodeEscapedDeep(item)) as T;
    }
    if (value !== null && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(([key, item]) => [
          key,
          UString.decodeEscapedDeep(item),
        ]),
      ) as T;
    }
    return value;
  }

  public static firstLetterCapital(text: string): string {
    return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
  }

  /**
   * Very very unlikely to generate duplicate under 10 millions simultaneous usages
   */
  public static generateUniqueString(middleStr: string = ""): string {
    const hash = createHash("md5").update(middleStr).digest("hex");

    return (
      Date.now().toString(36) +
      hash +
      Math.random().toString(36).substring(2, 5)
    ).toUpperCase();
  }

  public static generateRandomCode(length: number = 6): string {
    return Math.random()
      .toString(36)
      .substring(2, 2 + length)
      .padEnd(length, "0");
  }

  public static fingerprint(values: string[]): string {
    let hash = 2166136261;
    for (const value of values) {
      for (let i = 0; i < value.length; i++) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
      }
      hash ^= 31;
      hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
  }

  public static slugify(text: string): string {
    text = text.replace(/^\s+|\s+$/g, "").toLowerCase();

    const from =
      "ÁÄÂÀÃÅČÇĆĎÉĚËÈÊẼĔȆÍÌÎÏŇÑÓÖÒÔÕØŘŔŠŤÚŮÜÙÛÝŸŽáäâàãåčçćďéěëèêẽĕȇíìîïňñóöòôõøðřŕšťúůüùûýÿžþÞĐđßÆa·/_,:;";
    const to =
      "AAAAAACCCDEEEEEEEEIIIINNOOOOOORRSTUUUUUYYZaaaaaacccdeeeeeeeeiiiinnooooooorrstuuuuuyyzbBDdBAa------";

    for (let i = 0, l = from.length; i < l; i++) {
      text = text.replace(new RegExp(from.charAt(i), "g"), to.charAt(i));
    }

    return text
      .replace(/[^a-z0-9 -]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  public static removeEmojis(text: string): string {
    return text
      .replace(/[\u{1F600}-\u{1F64F}]/gu, "")
      .replace(/[\u{1F300}-\u{1F5FF}]/gu, "")
      .replace(/[\u{1F680}-\u{1F6FF}]/gu, "")
      .replace(/[\u{2600}-\u{26FF}]/gu, "")
      .replace(/[\u{2700}-\u{27BF}]/gu, "");
  }

  public static removeAccents(str: string) {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  public static removeStart(str: string, charCount: number): string {
    if (!str || charCount <= 0) {
      return str;
    }

    return str.substring(Math.min(charCount, str.length)).trim();
  }

  // Caps a long text while preserving both ends: keeps the head and the last
  // `tailLength` characters, dropping the middle. Used for LinkedIn profiles where
  // the high-value sections (experience, skills, contact block) sit at the very end,
  // after a long activity feed we only want a sample of.
  public static truncateHeadTail(text: string, maxLength: number, tailLength: number): string {
    if (text.length <= maxLength) {
      return text;
    }

    const headLength = Math.max(0, maxLength - tailLength);
    const head = text.slice(0, headLength).trimEnd();
    const tail = text.slice(-tailLength).trimStart();

    return `${head}\n[...]\n${tail}`;
  }
}
